import {usePromotion} from '@/context/Promotion/PromotionContext'
import {combineLocalDateAndTimeToUtc, splitUtcToLocalDate} from '@/helpers/dateUtils.js'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {getTimezoneByCountryCode} from '@/utils/constants/timeZones'
import {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'

export const usePromotionAttributesStep = () => {
  const {promotionType, promotion, onResultChange, promotionConfig} = usePromotion()
  const countryCode = useSelector(selectCountryCode)
  const timezone = getTimezoneByCountryCode(countryCode)
  const activeStartDateDisplay = splitUtcToLocalDate(promotion.activeStartDate)
  const activeEndDateDisplay = splitUtcToLocalDate(promotion.activeEndDate)

  const frequencyOptions = useMemo(
    () =>
      promotionConfig?.resetFrequency?.map(frequency => ({
        displayText: frequency,
        id: frequency.split(' ')[0],
        isBadge: false,
      })) || [],
    [promotionConfig?.resetFrequency]
  )

  const behaviorOptions = useMemo(
    () => promotionConfig?.behavior?.map(behavior => ({displayText: behavior, id: behavior, isBadge: false})) || [],
    [promotionConfig?.behavior]
  )

  useEffect(() => {
    const needsDefaults = promotion.resetFrequencyInDays === 0 || !promotion.behavior

    if (!needsDefaults) return

    const updates = {}

    if (promotion.resetFrequencyInDays === 0) {
      updates.resetFrequencyInDays = parseInt(frequencyOptions[0]?.id)
    }

    if (!promotion.behavior) {
      updates.behavior = behaviorOptions[0]?.displayText
    }

    if (Object.keys(updates).length > 0) {
      onResultChange(updates)
    }
  }, [behaviorOptions, frequencyOptions, promotion.resetFrequencyInDays, promotion.behavior, onResultChange])

  const handleInputChange = (value, field) => {
    onResultChange({[field]: value})
  }

  const handleDateChange = (formattedDate, field) => {
    if (!formattedDate) return

    const dateOnly = formattedDate.includes('T') ? formattedDate.split('T')[0] : formattedDate

    if (field === 'activeStartDate') {
      const combined = combineLocalDateAndTimeToUtc({
        date: dateOnly,
        time: promotion.activeStartTime || '00:00:00',
        timezone,
      })
      onResultChange({activeStartDate: combined})
    } else if (field === 'activeEndDate') {
      const combined = combineLocalDateAndTimeToUtc({
        date: dateOnly,
        time: promotion.activeEndTime || '00:00:00',
        timezone,
      })
      onResultChange({activeEndDate: combined})
    } else {
      onResultChange({[field]: dateOnly})
    }
  }

  const handleTimeChange = (formattedTime, timeField, dateField) => {
    const rawDate = dateField === 'activeStartDate' ? activeStartDateDisplay : activeEndDateDisplay
    const updates = {[timeField]: formattedTime}

    if (rawDate) {
      const combined = combineLocalDateAndTimeToUtc({
        date: rawDate,
        time: formattedTime || '00:00:00',
        timezone,
      })
      if (combined) {
        updates[dateField] = combined
      }
    }

    onResultChange(updates)
  }

  const handleDropdownChange = (value, field) => {
    if (!value?.length) return
    if (field === 'resetFrequencyInDays') {
      onResultChange({[field]: parseInt(value[0].id)})
    } else if (field === 'behavior') {
      onResultChange({[field]: value[0].displayText})
    }
  }

  const getSelectedFrequencyOption = () => {
    const option = frequencyOptions.find(opt => opt.id === promotion.resetFrequencyInDays?.toString())
    return option ? [option] : frequencyOptions.length > 0 ? [frequencyOptions[0]] : []
  }

  const getSelectedBehaviorOption = () => {
    const option = behaviorOptions.find(opt => opt.displayText === promotion.behavior)
    return option ? [option] : behaviorOptions.length > 0 ? [behaviorOptions[0]] : []
  }

  const rewardType = promotion.promotionConditions?.[0]?.rewards?.[0]?.type

  return {
    promotionType,
    promotion,
    activeStartDateDisplay,
    activeEndDateDisplay,
    frequencyOptions,
    behaviorOptions,
    rewardType,
    handleInputChange,
    handleDateChange,
    handleTimeChange,
    handleDropdownChange,
    getSelectedFrequencyOption,
    getSelectedBehaviorOption,
  }
}
